// src/messaging/NotesPanel.jsx
// ノート（会話に残しておく長い文）【2026-10-16】
//   グループ・授業グループ・1対1で使える（先生とのやり取りでは使わない）。
//   一覧 → 開く（本文・添付・コメント）。作った人は直す・消す・添付（4つまで）ができる。
//   signal が変わったら（リアルタイムの 'note' の合図）取り直す。
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { msgApi } from './api';
import { dateTime, prepareAttachment } from './util';
import { Modal, AuthImage, FileChip, ReportDialog } from './ui';
import { confirmDialog } from '../components/ConfirmDialog';

const NOTE_MAX = 10000;
const COMMENT_MAX = 1000;
const MAX_ATTACH = 4;

function NoteForm({ initial, onCancel, onSave, busy, err }) {
  const [title, setTitle] = useState((initial && initial.title) || '');
  const [text, setText] = useState((initial && initial.text) || '');
  return (
    <div>
      <div className="form-row">
        <label htmlFor="msg-note-title">タイトル（なくてもかまいません）</label>
        <input id="msg-note-title" type="text" maxLength={100} value={title} onChange={e => setTitle(e.target.value)} />
      </div>
      <div className="form-row">
        <label htmlFor="msg-note-text">本文</label>
        <textarea id="msg-note-text" rows={10} maxLength={NOTE_MAX} value={text} onChange={e => setText(e.target.value)} />
        <small className="msg-count">{text.length}/{NOTE_MAX}</small>
      </div>
      {err && <p className="msg-error">{err}</p>}
      <div className="msg-row-buttons">
        <button type="button" className="btn-secondary" onClick={onCancel}>やめる</button>
        <button type="button" className="btn-primary" disabled={busy || !text.trim()}
                onClick={() => onSave({ title: title.trim(), text: text.trim() })}>{busy ? '保存中…' : '保存する'}</button>
      </div>
    </div>
  );
}

export default function NotesPanel({ conversationId, writable, signal, onClose }) {
  const [view, setView] = useState({ mode: 'list' });  // list / create / detail(id) / edit(id)
  const [list, setList] = useState(null);
  const [note, setNote] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');
  const [report, setReport] = useState(null);
  const [info, setInfo] = useState('');
  const fileRef = useRef(null);

  const loadList = useCallback(async () => {
    const r = await msgApi.get(`/conversations/${conversationId}/notes`);
    if (r.success) setList(r.notes); else { setList([]); setErr(r.message || '読み込めませんでした'); }
  }, [conversationId]);

  const loadNote = useCallback(async (id) => {
    const r = await msgApi.get(`/notes/${id}`);
    if (r.success) setNote(r.note);
    else { setNote(null); setErr(r.message || 'ノートが見つかりません'); setView({ mode: 'list' }); }
  }, []);

  useEffect(() => { loadList(); }, [loadList, signal]);
  useEffect(() => {
    if (view.mode === 'detail') loadNote(view.id);
  }, [view, loadNote, signal]);

  const open = (id) => { setErr(''); setInfo(''); setNote(null); setView({ mode: 'detail', id }); };

  const create = async (v) => {
    setBusy(true); setErr('');
    const r = await msgApi.post(`/conversations/${conversationId}/notes`, { title: v.title || undefined, text: v.text });
    setBusy(false);
    if (!r.success) { setErr(r.message || '作れませんでした'); return; }
    loadList();
    open(r.note.id);
  };

  const update = async (v) => {
    setBusy(true); setErr('');
    const r = await msgApi.patch(`/notes/${view.id}`, { title: v.title || null, text: v.text });
    setBusy(false);
    if (!r.success) { setErr(r.message || '保存できませんでした'); return; }
    loadList();
    open(view.id);
  };

  const remove = async () => {
    if (!(await confirmDialog('このノートを削除しますか？添付とコメントも見えなくなります。'))) return;
    const r = await msgApi.del(`/notes/${note.id}`);
    if (!r.success) { setErr(r.message || '削除できませんでした'); return; }
    setView({ mode: 'list' }); setNote(null); loadList();
  };

  const addAttachment = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setErr('');
    const p = await prepareAttachment(f);
    if (p.error) { setErr(p.error); return; }
    setBusy(true);
    const r = await msgApi.upload(`/notes/${note.id}/attachments`, p.file);
    setBusy(false);
    if (!r.success) { setErr(r.message || '付けられませんでした'); return; }
    loadNote(note.id);
  };

  const removeAttachment = async (a) => {
    if (!(await confirmDialog('この添付を外しますか？'))) return;
    const r = await msgApi.del(`/notes/${note.id}/attachments/${a.id}`);
    if (!r.success) { setErr(r.message || '外せませんでした'); return; }
    loadNote(note.id);
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setBusy(true); setErr('');
    const r = await msgApi.post(`/notes/${note.id}/comments`, { text: comment.trim() });
    setBusy(false);
    if (!r.success) { setErr(r.message || 'コメントできませんでした'); return; }
    setComment('');
    loadNote(note.id);
  };

  const removeComment = async (c) => {
    if (!(await confirmDialog('このコメントを削除しますか？'))) return;
    const r = await msgApi.del(`/notes/${note.id}/comments/${c.id}`);
    if (!r.success) { setErr(r.message || '削除できませんでした'); return; }
    loadNote(note.id);
  };

  let title = 'ノート';
  let body = null;

  if (view.mode === 'create') {
    title = '新しいノート';
    body = <NoteForm onCancel={() => setView({ mode: 'list' })} onSave={create} busy={busy} err={err} />;
  } else if (view.mode === 'edit' && note) {
    title = 'ノートを直す';
    body = <NoteForm initial={note} onCancel={() => open(note.id)} onSave={update} busy={busy} err={err} />;
  } else if (view.mode === 'detail') {
    title = 'ノート';
    body = !note ? <p className="msg-help">読み込み中...</p> : (
      <div className="msg-note-detail">
        <button type="button" className="btn-link" onClick={() => { setView({ mode: 'list' }); setErr(''); }}>← 一覧へ戻る</button>
        <h4 className="msg-note-title">{note.title || '（タイトルなし）'}</h4>
        <p className="msg-note-meta">
          {note.author ? note.author.name : '（退会したユーザー）'}　{dateTime(note.created_at)}
          {note.edited_at && <>　編集済み</>}
        </p>
        <div className="msg-note-text">{note.text}</div>
        {note.attachments.length > 0 && (
          <div className="msg-note-attachments">
            {note.attachments.map(a => (
              <div key={a.id} className="msg-note-att">
                {a.kind === 'image' ? <AuthImage url={a.url} className="msg-note-img" /> : <FileChip file={a} onError={setErr} />}
                {note.mine && writable && <button type="button" className="btn-link danger" onClick={() => removeAttachment(a)}>外す</button>}
              </div>
            ))}
          </div>
        )}
        {info && <p className="msg-help strong">{info}</p>}
        {err && <p className="msg-error">{err}</p>}
        <div className="msg-row-buttons left">
          {note.mine && writable && <button type="button" className="btn-secondary" onClick={() => setView({ mode: 'edit', id: note.id })}>直す</button>}
          {note.mine && writable && note.attachments.length < MAX_ATTACH && (
            <>
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>
                画像・PDF を付ける（あと{MAX_ATTACH - note.attachments.length}つ）
              </button>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={addAttachment} />
            </>
          )}
          {note.mine && <button type="button" className="btn-danger" onClick={remove}>削除</button>}
          {!note.mine && <button type="button" className="btn-link danger" onClick={() => setReport({ note_id: note.id })}>このノートを通報</button>}
        </div>

        <h5 className="msg-note-comments-h">コメント（{note.comments.length}）</h5>
        <ul className="msg-note-comments">
          {note.comments.map(c => (
            <li key={c.id}>
              <div className="msg-note-meta">
                {c.author ? c.author.name : '（退会したユーザー）'}　{dateTime(c.created_at)}
                {c.mine
                  ? <button type="button" className="btn-link danger" onClick={() => removeComment(c)}>削除</button>
                  : <button type="button" className="btn-link danger" onClick={() => setReport({ note_comment_id: c.id })}>通報</button>}
              </div>
              <div className="msg-note-text">{c.text}</div>
            </li>
          ))}
        </ul>
        {writable && (
          <div className="msg-subform">
            <textarea rows={2} className="msg-field" maxLength={COMMENT_MAX} value={comment} onChange={e => setComment(e.target.value)}
                      placeholder="コメントを書く" aria-label="コメント" />
            <div className="msg-subform-foot">
              <span className="msg-count">{comment.length}/{COMMENT_MAX}</span>
              <button type="button" className="btn-primary" disabled={busy || !comment.trim()} onClick={addComment}>コメントする</button>
            </div>
          </div>
        )}
      </div>
    );
  } else {
    body = (
      <>
        {writable && (
          <div className="msg-row-buttons left">
            <button type="button" className="btn-primary" onClick={() => { setErr(''); setView({ mode: 'create' }); }}>新しいノート</button>
          </div>
        )}
        {err && <p className="msg-error">{err}</p>}
        {list === null ? <p className="msg-help">読み込み中...</p>
          : list.length === 0 ? <p className="msg-help">ノートはまだありません。流れてほしくない連絡や資料をまとめておけます。</p>
            : (
              <ul className="msg-note-list">
                {list.map(n => (
                  <li key={n.id}>
                    <button type="button" onClick={() => open(n.id)}>
                      <strong>{n.title || '（タイトルなし）'}</strong>
                      <span className="msg-note-excerpt">{n.excerpt}</span>
                      <span className="msg-note-meta">
                        {n.author ? n.author.name : ''}　{dateTime(n.created_at)}
                        {n.comment_count > 0 && <>　コメント {n.comment_count}</>}
                        {n.attachment_count > 0 && <>　添付 {n.attachment_count}</>}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
      </>
    );
  }

  return (
    <>
      <Modal title={title} onClose={onClose} wide>{body}</Modal>
      {report && (
        <ReportDialog target={report} what={report.note_id ? 'このノート' : 'このコメント'}
                      onClose={() => setReport(null)} onDone={setInfo} />
      )}
    </>
  );
}
